import { specToString } from 'elements/hardwareSelector/ComponentOverview'
import { HardwareComponentType } from 'elements/hardwareSelectorV2/types'
import { StandData } from 'opensolar-pageeditor/src/renderer/expoStand'
import { Button, DialogContent, styled } from 'opensolar-ui'
import { RowCentered } from 'projectSections/sections/manage3/styles'
import { useTranslate } from 'ra-core'
import { FC, useMemo, useState } from 'react'
import TabContent, { TabType } from './TabContent'

export interface ContentProps {
  component: HardwareComponentType
  standData?: StandData
  isStandDataLoading?: boolean
}

const ContentWrapper = styled(DialogContent, { name: 'ContentWrapper' })({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
})

const TabButton = styled(Button, { name: 'TabButton' })<{
  isSelected: boolean
}>(({ isSelected, theme }) => ({
  color: `${isSelected ? theme.palette.primary.contrastText : theme.palette.grey[600]} !important`,
  fontWeight: 500,
  margin: 20,
  borderRadius: 0,
  borderColor: isSelected ? '#4DC0AC' : 'transparent',
  borderBottomWidth: 4,
  borderBottomStyle: 'solid',
}))

const parseComponentData = (component: HardwareComponentType): Record<string, unknown> => {
  try {
    return component.data ? JSON.parse(component.data) : {}
  } catch {
    return {}
  }
}

const hasTechnicalData = (component: HardwareComponentType, parsedData: Record<string, unknown>): boolean => {
  return Object.keys(specToString).some((fieldKey) => {
    const value = component[fieldKey] || parsedData[fieldKey]
    return value !== null && value !== undefined
  })
}

const getModalTabs = (component: HardwareComponentType, parsedData: Record<string, unknown>) => {
  let tabs: TabType[] = []
  const hasProductDetails =
    (component.is_exhibitor && component.product_details) || component.component_content?.product_details
  const hasPdfContent =
    component.component_content?.product_details_is_pdf && component.component_content?.product_details_pdf_url
  if (hasProductDetails || hasPdfContent) tabs.push('Overview')
  if (hasTechnicalData(component, parsedData)) tabs.push('Technical')
  if (component.partner_expo_stand) tabs.push('Company')
  return tabs
}

const ModalContent: FC<ContentProps> = ({ component, standData, isStandDataLoading }) => {
  const translate = useTranslate()
  const parsedComponentData = useMemo(() => parseComponentData(component), [component.data])
  const modalTabs = useMemo(() => getModalTabs(component, parsedComponentData), [component, parsedComponentData])
  const [tabSelected, setTabSelected] = useState<TabType | null>(modalTabs[0])

  return (
    <ContentWrapper>
      <RowCentered>
        {modalTabs.map((tab) => (
          <TabButton
            key={'button-' + tab}
            onClick={() => {
              setTabSelected(tab)
            }}
            variant="text"
            color="secondary"
            isSelected={tab === tabSelected}
          >
            {translate(tab)}
          </TabButton>
        ))}
      </RowCentered>
      {tabSelected && (
        <TabContent
          tabSelected={tabSelected}
          component={component}
          parsedComponentData={parsedComponentData}
          standData={standData ?? undefined}
          isStandDataLoading={isStandDataLoading}
        />
      )}
    </ContentWrapper>
  )
}

export default ModalContent
